import { Subject, Observable, BehaviorSubject } from "rxjs";
import {
  Topic,
  ConnectionConfig,
  ConnectionState,
  WebSocketManagerConfig,
} from "./types";
import { WebSocketConnection } from "./WebSocketConnection";

interface ConnectionEntry {
  conn: WebSocketConnection;
  refCount: number;
}

interface ChannelEntry {
  subject: Subject<any>;
  refCount: number;
  endpoint: string;
  topic: Topic;
}

const channelKey = (endpoint: string, topic: Topic) => `${endpoint}::${topic}`;

// Defer the actual socket teardown by this long after refCount hits 0, so
// React StrictMode's mount→cleanup→mount and other rapid remounts (route
// changes, fast refresh) reuse the same socket instead of churning.
const TEARDOWN_GRACE_MS = 50;

class WebSocketManager {
  private connections: Map<string, ConnectionEntry> = new Map();
  private channels: Map<string, ChannelEntry> = new Map();
  private pendingTeardowns: Map<string, ReturnType<typeof setTimeout>> =
    new Map();
  private connected$ = new BehaviorSubject<boolean>(false);

  private getToken: () => string | null = () => null;
  private buildUrl: (endpoint: string) => string = (e) => e;
  private configured = false;

  /**
   * Must be called once at app startup before any connections are opened.
   *
   * Example (cloud-ui):
   *   wsManager.configure({
   *     getToken: () => localStorage.getItem("pulsejwt"),
   *     buildUrl: (endpoint) => `${grab_base_url(window.location.origin, "wss")}${endpoint}`,
   *   });
   */
  configure(config: WebSocketManagerConfig) {
    this.getToken = config.getToken;
    this.buildUrl = config.buildUrl;
    this.configured = true;
  }

  connect(
    endpoint: string,
    config: Partial<ConnectionConfig> = {},
  ): Observable<boolean> {
    if (!this.configured) {
      console.warn(
        "[WSManager] wsManager.configure() has not been called. Call it once at app startup.",
      );
    }

    this.ensureConnection(endpoint, config);
    return this.connected$.asObservable();
  }

  subscribe<T = any>(topic: Topic, endpoint: string): Observable<T> {
    const entry = this.ensureConnection(endpoint);
    entry.refCount += 1;

    const key = channelKey(endpoint, topic);
    let channel = this.channels.get(key);
    if (!channel) {
      channel = {
        subject: new Subject<T>(),
        refCount: 0,
        endpoint,
        topic,
      };
      this.channels.set(key, channel);
    }
    channel.refCount += 1;

    return channel.subject.asObservable() as Observable<T>;
  }

  unsubscribe(topic: Topic, endpoint: string) {
    const key = channelKey(endpoint, topic);
    const channel = this.channels.get(key);
    if (channel) {
      channel.refCount -= 1;
      if (channel.refCount <= 0) {
        channel.subject.complete();
        this.channels.delete(key);
      }
    }
    this.releaseConnection(endpoint);
  }

  send(endpoint: string, message: any): boolean {
    const entry = this.connections.get(endpoint);
    if (entry) {
      entry.conn.send(message);
      return true;
    }
    console.warn(`[WSManager] No connection found for endpoint: ${endpoint}`);
    return false;
  }

  disconnect(endpoint: string) {
    const pending = this.pendingTeardowns.get(endpoint);
    if (pending) {
      clearTimeout(pending);
      this.pendingTeardowns.delete(endpoint);
    }
    const entry = this.connections.get(endpoint);
    if (!entry) return;
    entry.conn.disconnect();
    this.connections.delete(endpoint);

    for (const [key, channel] of this.channels) {
      if (channel.endpoint === endpoint) {
        channel.subject.complete();
        this.channels.delete(key);
      }
    }

    this.refreshConnectedState();
  }

  disconnectAll() {
    for (const timer of this.pendingTeardowns.values()) {
      clearTimeout(timer);
    }
    this.pendingTeardowns.clear();

    for (const entry of this.connections.values()) {
      entry.conn.disconnect();
    }
    this.connections.clear();

    for (const channel of this.channels.values()) {
      channel.subject.complete();
    }
    this.channels.clear();

    this.connected$.next(false);
  }

  isConnected(): Observable<boolean> {
    return this.connected$.asObservable();
  }

  getConnectionState(endpoint: string): Observable<ConnectionState> | null {
    return this.connections.get(endpoint)?.conn.getState() ?? null;
  }

  private ensureConnection(
    endpoint: string,
    config: Partial<ConnectionConfig> = {},
  ): ConnectionEntry {
    const pending = this.pendingTeardowns.get(endpoint);
    if (pending) {
      clearTimeout(pending);
      this.pendingTeardowns.delete(endpoint);
    }

    const existing = this.connections.get(endpoint);
    if (existing) {
      if (!existing.conn.isConnected()) {
        existing.conn.connect();
      }
      return existing;
    }

    const conn = new WebSocketConnection(
      endpoint,
      endpoint,
      config,
      (msg, connId) => this.handleMessage(msg, connId),
      this.getToken,
      this.buildUrl,
    );

    const entry: ConnectionEntry = { conn, refCount: 0 };
    this.connections.set(endpoint, entry);

    conn.getState().subscribe(() => this.refreshConnectedState());
    conn.connect();
    return entry;
  }

  private releaseConnection(endpoint: string) {
    const entry = this.connections.get(endpoint);
    if (!entry) return;
    entry.refCount -= 1;
    if (entry.refCount > 0) return;
    if (this.pendingTeardowns.has(endpoint)) return;

    const timer = setTimeout(() => {
      this.pendingTeardowns.delete(endpoint);
      const current = this.connections.get(endpoint);
      if (!current || current.refCount > 0) return;
      current.conn.disconnect();
      this.connections.delete(endpoint);
      this.refreshConnectedState();
    }, TEARDOWN_GRACE_MS);
    this.pendingTeardowns.set(endpoint, timer);
  }

  private handleMessage(msg: any, endpoint: string) {
    for (const channel of this.channels.values()) {
      if (channel.endpoint === endpoint) {
        channel.subject.next(msg);
      }
    }
  }

  private refreshConnectedState() {
    const any = Array.from(this.connections.values()).some((e) =>
      e.conn.isConnected(),
    );
    if (this.connected$.value !== any) {
      this.connected$.next(any);
    }
  }
}

export { WebSocketManager };
export const wsManager = new WebSocketManager();
